import React, { useState, useEffect } from 'react';

function DBConnectionTest() {
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Connecting to backend...');
  const [dbInfo, setDbInfo] = useState(null);

  useEffect(() => {
    const testConnection = async () => {
      try {
        const response = await fetch('/api/test-db');
        const data = await response.json();
        
        if (data.status === 'success') {
          setStatus('success');
          setMessage(data.message);
          setDbInfo(data.info);
        } else {
          setStatus('error');
          setMessage(data.message + (data.error ? `: ${data.error}` : ''));
        }
      } catch (error) {
        setStatus('error');
        setMessage(`Failed to reach backend server. Error: ${error.message}`);
      }
    };

    testConnection();
  }, []);

  return (
    <>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');
          
          .db-test-wrapper {
            min-height: 100vh;
            width: 100vw;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);
            font-family: 'Outfit', sans-serif;
            color: #f8fafc;
            padding: 2rem;
            box-sizing: border-box;
          }

          .glass-panel {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(16px);
            -webkit-backdrop-filter: blur(16px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 24px;
            padding: 3rem;
            width: 100%;
            max-width: 800px;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
            animation: float 6s ease-in-out infinite;
          }

          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }

          .header-title {
            font-size: 2.5rem;
            font-weight: 700;
            background: linear-gradient(to right, #38bdf8, #818cf8);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 2rem;
            text-align: center;
            letter-spacing: -0.02em;
          }

          .status-badge {
            display: inline-flex;
            align-items: center;
            padding: 1rem 2rem;
            border-radius: 9999px;
            font-weight: 500;
            font-size: 1.1rem;
            margin-bottom: 3rem;
            width: 100%;
            justify-content: center;
            box-sizing: border-box;
            transition: all 0.3s ease;
          }

          .status-loading {
            background: rgba(255, 255, 255, 0.1);
            color: #f8fafc;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }

          .status-success {
            background: rgba(16, 185, 129, 0.1);
            color: #34d399;
            border: 1px solid rgba(16, 185, 129, 0.2);
            box-shadow: 0 0 20px rgba(16, 185, 129, 0.2);
          }

          .status-error {
            background: rgba(239, 68, 68, 0.1);
            color: #f87171;
            border: 1px solid rgba(239, 68, 68, 0.2);
            box-shadow: 0 0 20px rgba(239, 68, 68, 0.2);
          }

          .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1.5rem;
            margin-bottom: 2.5rem;
            animation: fadeIn 0.5s ease-out forwards;
          }

          .info-card {
            background: rgba(0, 0, 0, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.05);
            border-radius: 16px;
            padding: 1.5rem;
            transition: transform 0.3s ease, background 0.3s ease;
          }

          .info-card:hover {
            transform: translateY(-5px);
            background: rgba(255, 255, 255, 0.03);
            border-color: rgba(129, 140, 248, 0.3);
          }

          .info-label {
            font-size: 0.875rem;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            margin-bottom: 0.5rem;
          }

          .info-value {
            font-size: 1.25rem;
            font-weight: 600;
            color: #f8fafc;
            word-break: break-all;
          }

          .tables-container {
            background: rgba(0, 0, 0, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.05);
            border-radius: 16px;
            padding: 2rem;
            animation: fadeIn 0.7s ease-out forwards;
          }

          .tables-title {
            font-size: 1.25rem;
            font-weight: 600;
            color: #e2e8f0;
            margin-bottom: 1.5rem;
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .table-list {
            display: flex;
            flex-wrap: wrap;
            gap: 1rem;
            list-style: none;
            padding: 0;
            margin: 0;
          }

          .table-item {
            background: rgba(129, 140, 248, 0.1);
            color: #818cf8;
            border: 1px solid rgba(129, 140, 248, 0.2);
            padding: 0.5rem 1.25rem;
            border-radius: 9999px;
            font-size: 0.95rem;
            font-weight: 500;
            transition: all 0.2s ease;
            cursor: default;
          }

          .table-item:hover {
            background: rgba(129, 140, 248, 0.2);
            transform: scale(1.05);
          }

          .empty-tables {
            color: #94a3b8;
            font-style: italic;
          }

          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      
      <div className="db-test-wrapper">
        <div className="glass-panel">
          <h2 className="header-title">System Status</h2>
          
          <div className={`status-badge status-${status}`}>
            {status === 'loading' && <span style={{marginRight: '12px'}}>⚡</span>}
            {status === 'success' && <span style={{marginRight: '12px'}}>✨</span>}
            {status === 'error' && <span style={{marginRight: '12px'}}>⚠️</span>}
            {message}
          </div>

          {status === 'success' && dbInfo && (
            <>
              <div className="info-grid">
                <div className="info-card">
                  <div className="info-label">Database Name</div>
                  <div className="info-value">{dbInfo.database || 'N/A'}</div>
                </div>
                <div className="info-card">
                  <div className="info-label">MySQL Version</div>
                  <div className="info-value">{dbInfo.version || 'N/A'}</div>
                </div>
              </div>

              <div className="tables-container">
                <h3 className="tables-title">
                  <span>🗄️</span> Database Tables ({dbInfo.tables ? dbInfo.tables.length : 0})
                </h3>
                {dbInfo.tables && dbInfo.tables.length > 0 ? (
                  <ul className="table-list">
                    {dbInfo.tables.map((table, idx) => (
                      <li key={idx} className="table-item">{table}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="empty-tables">No tables found in this database.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default DBConnectionTest;
